import { distinctUntilChanged } from 'rxjs';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, EventEmitter, Input, Output } from '@angular/core';
import type { OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { type AbstractControl, ControlContainer, FormGroupDirective } from '@angular/forms';
import type { MultiSelectChangeEvent } from 'primeng/multiselect';
import * as FlatModels from '@medsurf/flat-models';
import * as FlatFacades from '@medsurf/flat-facades';
import { MediaControlService } from '@medsurf/flat-services';

@Component({
    selector: 'medsurf-design-form-control-input-node-tree-select',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './node-tree-select.input.form-control.design.html',
    styleUrls: ['./node-tree-select.input.form-control.design.scss'],
    viewProviders: [
        {
            provide: ControlContainer,
            useExisting: FormGroupDirective
        }
    ],
    standalone: false
})
export class NodeTreeSelectInputFormControlDesign implements OnInit, OnChanges {
  @Input() formField = true;
  @Input({ required: true }) control!: AbstractControl;
  @Input() inputClass: FlatModels.TextDesignModels.TextDesignClass = FlatModels.TextDesignModels.TextDesignClass.DEFAULT;
  @Input() maxSelectedLabels = 5;
  @Input() filter = false;
  @Input() propagateSelectionUp = false;
  @Input() propagateSelectionDown = false;
  @Input() placeholder = "";
  @Input() label?: string;
  @Input() disabled = false;
  @Input() autofocus = false;
  @Output() valueChange = new EventEmitter<MultiSelectChangeEvent>();
  
  name?: string;
  nodeTitleArray!: { name: string, value: string }[];
  readonly FlatModels = FlatModels;

  constructor(public nodeSharedFacade: FlatFacades.NodeSharedFacade,
              public slideAuthorFacade: FlatFacades.SlideAuthorFacade,
              public appSharedFacade: FlatFacades.AppSharedFacade,
              public mediaControlService: MediaControlService,
              private cd: ChangeDetectorRef, 
              private destroyRef: DestroyRef) {
    // Update Node Title Array
    this.slideAuthorFacade.nodeTitleArray$.pipe(takeUntilDestroyed()).subscribe((nodeTitleArray) => {
      this.nodeTitleArray = nodeTitleArray;
    });
  }

  ngOnInit() {
    const formGroup = this.control.parent;
    this.name = formGroup ? Object.keys(formGroup.controls).find(name => this.control === formGroup.get(name)) : undefined;

    this.control?.statusChanges.pipe(takeUntilDestroyed(this.destroyRef), distinctUntilChanged()).subscribe(() => {
      this.cd.markForCheck();
    });

    if (this.name === undefined) {
      throw new FlatModels.ErrorControlModels.MedsurfError(
        'notification.form_control_name_not_found',
        FlatModels.ErrorControlModels.ErrorLevel.enum.error,
        FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
        "LIBS.FlatDesign.NodeTreeSelectInputFormControlDesign.ngOnInit"
      );
    }
  }
  
  getColor(themeColor: string) {
    return themeColor && FlatModels.ColorConfigModels.themeColorList.find(tcl => tcl.value === themeColor)?.hexValue || themeColor;
  }
  
  getSolid(themeColor: string) {
    return themeColor && themeColor.length > 0 ? FlatModels.ButtonDesignModels.ButtonSolidStyle : '';
  }
  
  onTypedNodeExpandedChange(typedNodeExpanded: { [id: string]: boolean }) {
    this.slideAuthorFacade.requestSetTypedNodeExpanded(typedNodeExpanded);
  }
  
  onTypedPageExpandedChange(typedPageExpanded: { [id: string]: boolean }) {
    this.slideAuthorFacade.requestSetTypedPageExpanded(typedPageExpanded);
  }
  
  onTypedLayerExpandedChange(typedLayerExpanded: { [id: string]: boolean }) {
    this.slideAuthorFacade.requestSetTypedLayerExpanded(typedLayerExpanded);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['disabled']?.currentValue) {
      this.control?.disable();
    } else {
      this.control?.enable();
    }
  }
  
  onSwitchChange(routeElement: FlatModels.NodeEntityModels.RouteElement,
                 event: any) {
    const value = structuredClone(this.control.getRawValue() || []);
    if (event.checked) {
      value.push(routeElement.id);
    } else {
      value.splice(value.findIndex((v: string) => v === routeElement.id), 1);
    }
    this.control.patchValue(value);
    this.valueChange.emit({
      itemValue: routeElement,
      originalEvent: new Event('force'),
      value: value
    });
  }
  
  isInArray(routeElement: FlatModels.NodeEntityModels.RouteElement) {
    const value = this.control.getRawValue() || [];
    return value.findIndex((v: string) => v === routeElement.id) > -1;
  }
}
